import React, { useState, useEffect, useRef, useMemo } from 'react'

import { getErrorMessage, isCertificateInTransferState, getTransferDisabledMessage, getTransferBlockedReason } from 'utils'

import type { CertificateAdmin, Customer } from 'types'

import { useSnackbar } from 'contexts'
import {
  useCertificateMutations,
  useCertificateBulkTransfer,
  useTransferableCertificates,
  useCustomers,
} from 'hooks/api'

import { Box, Stack, Typography, Divider, ListItem, ListItemButton } from '@mui/material'

import { Modal, Button, Alert } from '@repo/ui'
import { suggestEmailCorrection, validateEmailAddress } from '@repo/types'
import { OwnerPreview, CertificatePreview, InvitePreview, SearchFilter } from 'components'


/**
 * The ONLY status `POST /transfer/admin/init/:certificateId` accepts.
 * `transfer-certificate-to-owner.use-case.ts` 400s everything else — the four
 * in-transfer states with "already in a transfer state", the rest with 'should be
 * in "Inactive" status'. Kept as a named predicate because both the single and the
 * bulk path have to agree with the server on it, and they used to disagree (the UI
 * once tested `=== 'active'`, the exact inverse, so every Transfer it offered 400'd
 * after submit).
 */
const isTransferable = (certificate: Pick<CertificateAdmin, 'status'>) =>
  certificate.status === 'inactive'


/**
 * VRG-471 — was a local `EMAIL_RE` shared by copy with the app's gift picker.
 * Both were syntax-only, which is exactly how `spencer@usevergence.cin` reached
 * the server, provisioned an owner and had two undeliverable emails sent to it.
 *
 * Now the shared `@repo/types` validator, so this modal, the app's gift picker
 * and the API's `@IsDeliverableEmailDomain()` all read the SAME table of real
 * TLDs. The old comment said the real check was `@IsEmail()` on the server; the
 * point of this change is that `@IsEmail()` was never a sufficient check.
 */
const inspectRecipient = (text: string) => {
  const trimmed = text.trim()
  const result = validateEmailAddress(trimmed)
  const suggestion = suggestEmailCorrection(trimmed)

  return {
    /** Syntactically an address — decides whether to offer the invite row at all. */
    looksLikeEmail: result.ok || result.code === 'unknown_tld',
    /** A real, deliverable ending. */
    isDeliverable: result.ok,
    reason: result.message,
    /**
     * Advisory ONLY. Present for a bad ending AND for a plausible typo of a real
     * domain, and in the second case the typed address stays fully submittable —
     * an unusual-but-real domain must never be blocked by a guess.
     */
    suggestion,
    /**
     * Is there anything to say about this address that the user should see BEFORE
     * it becomes the recipient? Either the ending cannot receive mail, or it can
     * but looks like a slip for a domain we know.
     *
     * This is asked when the user ACTS on the invite row, never while they type —
     * see the `listFooter` comment below for why a keystroke is not evidence.
     */
    isQuestionable:
      !result.ok || Boolean(suggestion && suggestion.full !== trimmed),
  }
}


/**
 * ONE SENTENCE PER CAUSE THE SERVER CAN ACTUALLY DISTINGUISH.
 *
 * Derived from `BulkTransferFailureCode`, which is in turn derived from the typed
 * errors `transfer-certificate-to-owner.use-case.ts` throws — so this table cannot
 * name a cause the server is not in a position to report.
 *
 * WHAT THIS REPLACED, AND WHY IT MATTERED (VERA-304). Every non-transferred
 * certificate used to produce the single line "Their status changed before the
 * transfer ran. Refresh and try again." That sentence is only true for
 * `INVALID_STATUS`. The use-case's four `CertificateGeneralError` throws include two
 * AUTHORIZATION refusals, and for those the message sent the user to refresh a page
 * that was never stale, while the real answer — they have no rights over that
 * certificate — was never shown. An error that confidently names the wrong cause is
 * worse than one that declines to name a cause, because it spends the user's time on
 * a remedy that cannot work.
 *
 * So: name the causes the server can distinguish, and for anything else fall back to
 * `UNKNOWN`'s deliberately non-committal line plus whatever reason the server sent.
 * Severity follows recoverability — a stale row is a `warning` (re-select and retry);
 * everything else is an `error`, because no change to the selection fixes it.
 */
const FAILURE_COPY: Record<
  string,
  { severity: 'warning' | 'error'; text: string }
> = {
  INVALID_STATUS: {
    severity: 'warning',
    text: 'Their status changed before the transfer ran. Refresh and try again.',
  },
  NOT_PERMITTED: {
    severity: 'error',
    text: 'You do not have permission to transfer them. Ask an admin for access.',
  },
  NOT_FOUND: {
    severity: 'error',
    text: 'They could not be found. They may have been deleted.',
  },
  INTERNAL_ERROR: {
    severity: 'error',
    text: 'Something went wrong on our side. Try again shortly.',
  },
}

/**
 * The honest default. States only what is certainly true — they did not move — and
 * lets the server's own reason carry the specifics, rather than inventing a cause.
 */
const UNKNOWN_FAILURE_TEXT = 'They could not be transferred.'


interface TransferCertificateModalProps {
  certificate?: CertificateAdmin | null
  /**
   * BULK mode — the table's whole selection, qualified and unqualified alike.
   *
   * Deliberately the RAW selection rather than a pre-filtered list: a table
   * selection is arbitrary, so the modal has to be able to say "3 of the 5 you
   * picked can be transferred, and here is why the other 2 cannot" before the user
   * commits. Handing it only the 3 would leave it unable to mention the 2, which is
   * the silent-subset failure this mode exists to avoid.
   *
   * Mutually exclusive with `certificate` in practice: when this is non-empty the
   * modal renders the batch summary instead of the single-certificate picker.
   */
  certificates?: CertificateAdmin[] | null
  /**
   * VRG-720 — narrow the single-certificate picker to these ids.
   *
   * For the Leads surface, where the answer to "which certificate" is not "any of
   * the brand's" but "one of the ones this person actually scanned". The ids are
   * resolved SERVER-side (the hook passes them to `/certificates/admin`), still
   * filtered to the transferable statuses, so what comes back is the intersection
   * of "they scanned it" and "the endpoint will accept it".
   *
   * Three outcomes, and each is handled rather than collapsed:
   *   exactly one  -> preselected, so the common case is one click
   *   more than one -> the picker, listing only those
   *   none          -> an explanation, because an empty picker here means something
   *                    specific ("already claimed") and a brand would otherwise read
   *                    it as the modal being broken
   *
   * Distinct from `certificate`, which preselects a known one outright. Pass this
   * when you know the CANDIDATES but not the answer. Ignored in bulk mode.
   */
  restrictToCertificateIds?: string[] | null
  owner?: Customer | null
  /**
   * VRG-720 — preselect the recipient as a bare EMAIL rather than as an owner on file.
   *
   * The counterpart to `owner` for a caller who has an address and no customer record
   * behind it. The Leads surface is exactly that: a lead is defined by the query as
   * someone who is NOT an owner of this company, so there is no `Customer` to pass,
   * and the modal renders `InvitePreview` instead of `OwnerPreview` — which is the
   * honest card, since its copy ("Send to this email. Invite as new owner") names the
   * action without claiming anything about whether an account exists.
   *
   * Submits identically: both this and `owner` feed the one `toCustomerEmail` the
   * endpoint accepts, and the server provisions the owner for an address it does not
   * know.
   */
  inviteEmail?: string | null
  open: boolean
  onClose: () => void
  refetch: () => void
}

/**
 * Brand -> owner certificate transfer. Reached from four surfaces, each of which
 * preselects the half it already knows about:
 *
 *   Owners list (row kebab)      -> `owner`
 *   Owner detail (header)        -> `owner`
 *   Certificates list (kebab)    -> `certificate`
 *   Certificate detail (kebab)   -> `certificate`
 *
 * plus the Certificates page header, which preselects neither and asks for both.
 *
 * WHY THERE IS NO react-hook-form HERE ANY MORE (VERA-304).
 *
 * The submit used to gate on `!isDirty`, which is the wrong question. `isDirty`
 * asks "has the user typed something that is not yet saved" — the right gate for a
 * record EDIT form, per working-conventions §54. This is not an edit form; it is an
 * action composer, and the question it has to answer is "do I have the two things
 * the endpoint needs" — a certificate and a recipient. Those are the same question
 * only when the user filled both in by hand, which is exactly the case the four
 * preselecting surfaces above are built to avoid. Preseeding the recipient with
 * `setValue` (no `shouldDirty`) left the form pristine with both fields visibly
 * filled and Transfer permanently disabled; the only escape was pressing Change and
 * re-picking the same person.
 *
 * §54's own remedy for a stale `isDirty` is to seed with `reset(record)` instead of
 * `setValue`, and that WOULD have made `isDirty` compute correctly — correctly
 * FALSE, which still disables Transfer on a fully-populated form. So the defect was
 * never the seeding mechanism, it was the gate, and `shouldDirty: true` would only
 * have taught `isDirty` to lie in the useful direction.
 *
 * Removing the form rather than re-gating it is the seam fix: the recipient was
 * being held in THREE places (`owner` state, `inviteEmail` state, and RHF's `email`
 * field), kept in step by hand at every call site, and the submit read the one that
 * the preselect path never marked. There is one field, no validation and no error
 * rendering here, so RHF was buying nothing and costing a third source of truth.
 * Now `recipientEmail` is derived from the two states that already existed, the gate
 * reads it, and the submit reads it.
 */
export const TransferCertificateModal = (props: TransferCertificateModalProps) => {
  const { open, onClose = () => { }, refetch } = props
  const { open: notification } = useSnackbar()

  const [certificate, setCertificate] = useState<CertificateAdmin | null>(null)
  const [owner, setOwner] = useState<Customer | null>(null)

  /**
   * Batch mode and its split, derived from the selection the caller passed rather
   * than held in state — there is nothing to edit here, so storing a copy would
   * only create a second version of the truth that can drift from the table's.
   */
  const selection = props.certificates ?? []
  const isBulk = selection.length > 0

  const { transferable, excluded } = useMemo(() => {
    return {
      transferable: selection.filter(isTransferable),
      excluded: selection.filter(row => !isTransferable(row)),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.certificates])

  /**
   * Why each excluded certificate is excluded, grouped so the message names the
   * REASON and its count rather than listing rows.
   *
   * Uses `getTransferBlockedReason`, not `getTransferDisabledMessage`: the latter is
   * a full sentence about ONE certificate ("This certificate is waiting for
   * acceptance"), which reads wrong the moment you put a count in front of it —
   * "7: This certificate is waiting for acceptance". Both live in utils/certificate.ts
   * as two renderings of one fact, so the grouped phrasing cannot drift from the
   * per-row sentence the row action and detail page show.
   */
  const exclusionReasons = useMemo(() => {
    const byReason = new Map<string, number>()
    for (const row of excluded) {
      const reason =
        getTransferBlockedReason(row.status) ||
        'in a status that cannot be transferred'
      byReason.set(reason, (byReason.get(reason) ?? 0) + 1)
    }
    return Array.from(byReason.entries())
  }, [excluded])
  /**
   * A recipient addressed by a typed email that matched nobody in this brand's
   * customer list.
   *
   * Held separately from `owner` rather than as a synthetic `Customer` because the
   * two are genuinely different things and the UI has to say so: `owner` renders as
   * an existing person, this renders as an invitation. Both feed the SAME submit —
   * `toCustomerEmail` — because that is the only recipient field the admin transfer
   * endpoint accepts, and `SecurityService.getCustomerByEmail` is what turns an
   * unknown address into a provisioned owner on the server side.
   */
  const [inviteEmail, setInviteEmail] = useState<string | null>(null)

  /**
   * The address the user has TRIED to add and which the domain check had something
   * to say about. Its only job is to distinguish "still typing" from "asked for
   * this one", which is the whole of VRG-475.
   *
   * Held as the address rather than as a boolean so it self-clears: the footer
   * renders the confirm step only while this EQUALS what is currently typed, so
   * one more keystroke returns the row to its plain state with no effect, no
   * timer and no stale flag to reset. A boolean would need both.
   */
  const [questionedInvite, setQuestionedInvite] = useState<string | null>(null)

  /**
   * The single recipient value. Derived, never stored, so the two ways of naming a
   * recipient cannot disagree with what gets submitted.
   */
  const recipientEmail = owner?.email ?? inviteEmail ?? null

  // Captured at submit time so the success toast names the recipient even though
  // `onSuccess` fires after the state has been handed back to the caller.
  const submittedEmailRef = useRef<string | null>(null)

  // Preselect the opener's certificate every time the modal OPENS (keyed on `open`, not
  // just on the prop changing) — otherwise closing + reopening from the same certificate
  // page lost the preselection and fell back to the picker. handleClose resets on close.
  useEffect(() => {
    // Bulk mode has no single certificate to preselect, and must NOT be closed by the
    // guard below: a mixed selection is expected there and is reported in the summary
    // rather than treated as an error.
    if (isBulk) return
    if (open && props.certificate !== undefined) {
      // Validate that certificate is not already in transfer state
      if (props.certificate && isCertificateInTransferState(props.certificate.status)) {
        const message = getTransferDisabledMessage(props.certificate.status) || 'This certificate is already being transferred'
        notification({
          type: 'error',
          title: 'Cannot transfer certificate',
          text: message,
        })
        onClose()
        return
      }
      setCertificate(props.certificate)
    }
  }, [open, props.certificate, notification, onClose, isBulk])

  useEffect(() => {
    if (open && props.owner !== undefined) {
      setOwner(props.owner)
    }
  }, [open, props.owner])

  // Same shape as the owner preselect above, keyed on `open` for the same reason:
  // reopening from the same row has to restore the recipient, and `handleClose`
  // clears it. Mutually exclusive with `owner` in practice -- `recipientEmail`
  // prefers `owner` if a caller somehow passed both.
  useEffect(() => {
    if (open && props.inviteEmail) {
      setInviteEmail(props.inviteEmail)
    }
  }, [open, props.inviteEmail])

  const [{ transfer }, { isLoading: isTransferring }] = useCertificateMutations({
    onSuccess: () => {
      notification({
        type: 'success',
        title: `Certificate has been transferred to ${submittedEmailRef.current}`,
      })
      handleClose()
      refetch()
    },
    onError: error => {
      const message = getErrorMessage(error)

      notification({
        type: 'error',
        title: message,
      })
    },
  })

  /**
   * Bulk result reporting.
   *
   * The summary is reported EXACTLY as the server returned it — one toast per
   * non-zero bucket, never a blanket "transferred". A batch where 3 of 5 moved says
   * so; a batch where every certificate was refused reports an error and does NOT
   * close the modal, because nothing happened and closing would imply it did.
   *
   * Non-transferred certificates are reported BY CAUSE, not by bucket. Grouping on
   * `code` rather than on `skipped`/`failed` is what makes the sentence match the
   * reason: the buckets say how recoverable something is, the code says why it
   * happened, and only the second can be turned into a sentence. Reporting per
   * bucket is how an authorization refusal came to wear the stale-status message
   * (see `FAILURE_COPY`).
   *
   * A batch that hits two different causes gets two toasts, each with its own count.
   * That is the point — one merged "N could not be transferred" would force a single
   * explanation onto certificates that failed for different reasons, which is the
   * same defect one level up.
   */
  const { bulkTransfer, isLoading: isBulkTransferring } = useCertificateBulkTransfer({
    onSuccess: data => {
      const summary = data?.summary
      const transferred = summary?.transferred ?? 0
      const results = data?.results ?? []

      if (transferred > 0) {
        notification({
          type: 'success',
          title: `${transferred} certificate${transferred > 1 ? 's' : ''} transferred to ${submittedEmailRef.current}`,
        })
      }

      // Count per cause, preserving the first server message seen for that cause so
      // an unrecognised code still has something true to say.
      const byCause = new Map<string, { count: number; message?: string }>()
      for (const row of results) {
        if (row.status === 'transferred') continue
        const key = row.code ?? 'UNKNOWN'
        const entry = byCause.get(key)
        if (entry) entry.count++
        else byCause.set(key, { count: 1, message: row.message })
      }

      for (const [code, { count, message }] of byCause) {
        const known = FAILURE_COPY[code]
        notification({
          type: known?.severity ?? 'error',
          title: `${count} certificate${count > 1 ? 's' : ''} could not be transferred`,
          // `text` is the ONLY body key. `message` is not a member of NotificationProps
          // (SnackbarProvider.tsx), so the provider never reads it and the sentence is
          // dropped at runtime.
          //
          // An earlier revision of this comment said ~30 call sites across the platform
          // passed `message` and that they were "reported, not swept up here". Measured:
          // it was 14, three of them in THIS file — so the file was violating its own
          // note. All 14 were renamed to `text` (2026-08-25), which is why this is now a
          // statement about the codebase rather than a warning about it.
          //
          // Fixed by renaming the call sites rather than by teaching the provider to
          // accept `message` as an alias: two keys for one concept is the drift that
          // produced this in the first place, and an alias would have made the wrong key
          // permanently correct.
          text: known?.text ?? [UNKNOWN_FAILURE_TEXT, message].filter(Boolean).join(' '),
        })
      }

      // Nothing moved: keep the modal open so the recipient the user picked is not
      // thrown away, and so the outcome does not read as a success.
      if (transferred === 0) {
        refetch()
        return
      }

      handleClose()
      refetch()
    },
    onError: error => {
      notification({ type: 'error', title: getErrorMessage(error) })
    },
  })

  const handleClose = () => {
    setOwner(null)
    setInviteEmail(null)
    setQuestionedInvite(null)
    setCertificate(null)
    submittedEmailRef.current = null
    onClose()
  }

  const handleConfirmBulk = () => {
    if (!recipientEmail || transferable.length === 0) return

    submittedEmailRef.current = recipientEmail
    bulkTransfer({
      certificateIds: transferable.map(row => row.id),
      toCustomerEmail: recipientEmail,
    })
  }

  const handleConfirm = () => {
    if (isBulk) return handleConfirmBulk()

    if (!certificate || !recipientEmail) return

    // Double-check certificate status before transferring
    if (isCertificateInTransferState(certificate.status)) {
      const message = getTransferDisabledMessage(certificate.status) || 'This certificate is already being transferred'
      notification({
        type: 'error',
        title: 'Cannot transfer certificate',
        text: message,
      })
      return
    }

    submittedEmailRef.current = recipientEmail
    transfer({ certificateId: certificate.id, toCustomerEmail: recipientEmail })
  }

  const handleChangeOwner = () => {
    setOwner(null)
    setInviteEmail(null)
    setQuestionedInvite(null)
  }

  const handleSelectCertificate = (selectedCertificate: any) => {
    // Block certificates already mid-transfer.
    if (selectedCertificate && isCertificateInTransferState(selectedCertificate.status)) {
      const message = getTransferDisabledMessage(selectedCertificate.status) || 'This certificate is already being transferred'
      notification({
        type: 'error',
        title: 'Cannot transfer certificate',
        text: message,
      })
      return
    }
    setCertificate(selectedCertificate)
  }

  /**
   * Only certificates the endpoint will actually accept (`inactive`). The picker
   * used to list every status, so choosing an `active` one looked fine and returned
   * a 400 AFTER submitting — the late failure working-conventions §46 exists to
   * prevent. Fetched only while the picker is on screen.
   */
  const restrictToIds = props.restrictToCertificateIds
  const isRestricted = Array.isArray(restrictToIds)

  const { data: certificates, isLoading: isLoadingCertificates } =
    useTransferableCertificates({
      enabled: open && !certificate && !isBulk,
      certificateIds: restrictToIds,
    })

  /**
   * A restriction that resolves to exactly one certificate selects it.
   *
   * In the caller's hands this would mean fetching the certificate there purely to
   * hand it back as `certificate`, duplicating the request the picker already makes.
   * Here it costs nothing: the fetch has happened, and "one candidate" is the same
   * answer as "preselected".
   *
   * Guarded on `!certificate` so it fires once rather than fighting a user who
   * pressed Change -- though with one candidate there is nothing else to change to,
   * which is why Change is suppressed below in that case.
   */
  useEffect(() => {
    if (!open || isBulk || certificate || !isRestricted) return
    if (certificates.length === 1) setCertificate(certificates[0])
  }, [open, isBulk, certificate, isRestricted, certificates])

  // Restricted, loaded, and nothing came back: they scanned things, none of which
  // this endpoint will take. Said plainly rather than rendered as an empty picker.
  const restrictedToNone =
    isRestricted && !isLoadingCertificates && !certificate && certificates.length === 0

  // Owners for the "Transfer to" picker — same shared SearchFilter component the
  // Certificate field above uses (full visual + behavioral parity). Its scan control resolves
  // tag → certificate → owner server-side (`resolve-by-tags`), so scanning the item picks the
  // person who owns it.
  const { data: customersData } = useCustomers({ limit: 100, offset: 0, query: '' })
  const customers = (customersData?.data as Customer[]) || customersData || []

  // One in-flight flag for both paths, so Cancel and the submit button read the same
  // whichever mode is active.
  const isSubmitting = isTransferring || isBulkTransferring

  // Same question in both modes — "do I have the things the endpoint needs" — asked of
  // whichever certificate source is in play.
  const hasCertificates = isBulk ? transferable.length > 0 : Boolean(certificate)

  return (
    <Modal
      title={isBulk ? 'Transfer certificates' : 'Transfer certificate'}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={() => handleClose()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            disableElevation
            onClick={() => handleConfirm()}
            // "Do I have the two things the endpoint needs", not "has the user
            // touched the form" — see the block comment on this component.
            disabled={!hasCertificates || !recipientEmail || isSubmitting}
          >
            {isSubmitting
              ? 'Transferring...'
              : isBulk && transferable.length > 1
                ? `Transfer ${transferable.length}`
                : 'Transfer'}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {isBulk ? (
          /**
           * BATCH SUMMARY — read-only on purpose.
           *
           * The selection belongs to the table; this modal states what will happen to
           * it and takes the recipient. Making the list editable here would give the
           * same set two owners and let them disagree.
           *
           * The count and the exclusions are shown BEFORE the user commits, which is
           * the whole point: transferring the qualifying subset silently would leave
           * the user believing all N moved.
           */
          <Stack spacing={4}>
            {/**
              * The plain count heads the block ONLY when everything selected qualifies.
              * With exclusions present the Alert below states the same arithmetic in a
              * fuller sentence, and having both meant the modal opened with "1 of 5
              * certificates" immediately above "Only 1 of the 5 you selected can be
              * transferred" — the number twice, the meaning once.
              */}
            {excluded.length === 0 && (
              <Typography variant="body2" sx={{ fontSize: 16 }}>
                {`${transferable.length} certificate${transferable.length > 1 ? 's' : ''}`}
              </Typography>
            )}

            {excluded.length > 0 && (
              /**
               * THE COUNT LEADS; THE TAXONOMY DOES NOT.
               *
               * This used to read "4 of the 5 you selected cannot be transferred and
               * will be left as they are: 2 waiting for acceptance, 2 already
               * activated or no longer on the shelf." — one sentence carrying an
               * arithmetic fact, a reassurance, and a per-reason breakdown, which is
               * three jobs and reads as none of them.
               *
               * The count is the half that must stay, and it is stated the way the
               * user will check it: how many CAN move, out of how many they picked.
               * The failure this prevents is the silent subset — select 20, see 5
               * listed, and never register that 15 are missing — and that failure is
               * about the arithmetic, not about the reasons.
               *
               * The breakdown is demoted rather than dropped: an operator who sees
               * that 15 are staying behind will immediately ask why, and there is
               * nowhere else in this modal to find out (the list below shows only the
               * transferable rows). Quieter type, second line, still readable.
               *
               * "will be left as they are" is gone. Nothing here transfers them, so
               * saying they stay put restates the sentence before it.
               */
              <Alert
                severity="warning"
                description={
                  <Stack spacing={2}>
                    <Typography variant="body3">
                      {`Only ${transferable.length} of the ${selection.length} you selected can be transferred.`}
                    </Typography>
                    <Typography
                      variant="body3"
                      color="text.secondary"
                      sx={{ lineHeight: 1.5 }}
                    >
                      {exclusionReasons
                        .map(([reason, count]) => `${count} ${reason}`)
                        .join(', ') + '.'}
                    </Typography>
                  </Stack>
                }
              />
            )}

            {transferable.length > 0 && (
              <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
                <Stack spacing={3}>
                  {transferable.map(row => (
                    <CertificatePreview key={row.id} certificate={row} size="medium" />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        ) : certificate ? (
          <Stack spacing={4}>
            {/* No separate "Clear" link — the card's Change action already re-picks. */}
            <Typography variant="body2" sx={{ fontSize: 16 }}>Certificate</Typography>
            {/**
              * Change is withheld when a restriction left exactly ONE candidate, because
              * there is nothing to change to: pressing it would clear the selection and
              * drop the user into a picker holding the single row they just had. An
              * affordance whose only outcome is to undo itself reads as a dead control.
              */}
            <CertificatePreview
              certificate={certificate}
              {...(isRestricted && certificates.length <= 1
                ? {}
                : { onChange: () => setCertificate(null) })}
            />
          </Stack>
        ) : restrictedToNone ? (
          /**
            * They scanned something; none of it is the brand's to hand over.
            *
            * This is the one empty state that must NOT render as an empty picker. A
            * picker with no rows says "we found nothing", which a brand reads as a
            * search bug — whereas the actual fact is specific, expected, and the
            * reason the action exists at all: a certificate can only be transferred
            * while the brand still holds it, and an `active` one has already been
            * claimed by somebody.
            */
          <Alert
            severity="warning"
            description="Nothing to transfer. A certificate can only be handed over while you still hold it, and everything this person scanned has already been claimed by its owner."
          />
        ) : (
          // Same search-with-QR component the table filters use (Certificate / Owner
          // / ID …). Single-select: picking (or scanning) a certificate selects it.
          <SearchFilter
            column={{ headerName: 'Certificate' }}
            notFilter={true}
            singleSelect={true}
            rows={certificates}
            searchParams={{ fields: ['product.title', 'product_sku', 'id'], preview: 'certificates' }}
            onChange={handleSelectCertificate}
          />
        )}
        <Divider />
        {owner || inviteEmail ? (
          <Stack spacing={4}>
            {/* No separate "Clear" link — the card's Change action already re-picks. */}
            <Typography variant="body2" sx={{ fontSize: 16 }}>Transfer to</Typography>
            {/* The invite card, not the owner card, when the recipient is an
                address rather than a person on file. This is the last screen
                before Transfer, so it is where "they may not have an account"
                has to be visible. */}
            {inviteEmail ? (
              <InvitePreview email={inviteEmail} onChange={handleChangeOwner} />
            ) : (
              <OwnerPreview owner={owner} onChange={handleChangeOwner} />
            )}
          </Stack>
        ) : (
          // Same search-with-QR component the Certificate field above (and the
          // table filters) use — full parity. Single-select: picking OR scanning
          // an owner selects it. The scan target selector resolves tag → certificate →
          // owner through `resolve-by-tags`, the same lookup the Owner filter uses.
          // `label` keeps the field reading "Transfer to" while `column.headerName`
          // stays the noun the scan resolves by.
          <SearchFilter
            column={{ headerName: 'Owner' }}
            label="Transfer to"
            notFilter={true}
            singleSelect={true}
            rows={customers}
            searchParams={{ fields: ['name', 'email'], preview: 'owners' }}
            onChange={(items: any) => {
              const selected = Array.isArray(items) ? items[0] ?? null : items ?? null
              setInviteEmail(null)
              setQuestionedInvite(null)
              setOwner(selected)
            }}
            /**
             * YOU CAN TRANSFER TO SOMEONE WHO DOES NOT EXIST YET.
             *
             * The capability was already complete on the server and had no way in
             * from here: `POST /transfer/admin/init/:certificateId` resolves its
             * recipient through `SecurityService.getCustomerByEmail`, which
             * provisions the `users` + `owners` rows for an unknown address, sends
             * the Welcome mail alongside the transfer notice, and moves the
             * certificate to `waiting_accept`. This picker offered no way to reach
             * it — an unrecognised address showed "No results" and stopped.
             *
             * Mirrors `apps/app`'s gift picker (`RecipientSearchPicker`), which
             * solved the same problem: the row appears as soon as what you have
             * typed is a valid email, not only once the search has come back empty.
             * Waiting for zero results would hide it while the list still held
             * near-matches on the local part, which is exactly when someone is
             * typing a full address.
             *
             * ONE DELIBERATE DIFFERENCE from the app. There, an email query never
             * renders lookup results at all — VRG-357 made that a privacy
             * guarantee, since a result appearing for one address and not another
             * confirms which addresses have accounts. That reasoning does not
             * transfer: `/customers/admin` is company-scoped (`getAggregatesList`
             * requires a `companyId` and joins through `user_companies`), so an
             * admin only ever sees their own brand's customers and learns nothing
             * about anyone else's. So results still show here — and the invite row
             * is suppressed when one of them IS the typed address, because offering
             * both would present two routes to the same person while describing
             * only one of them accurately.
             */
            /**
             * THE DOMAIN CHECK FIRES ON THE ACT OF ADDING, NOT ON A KEYSTROKE (VRG-475).
             *
             * `search` is the LIVE input, so this function runs on every character.
             * Judging the address here judged it mid-word: typing `spencer@vera.inc`
             * was inspected at `spencer@vera.i`, `.i` is not a delegated TLD, and the
             * user was told their ending was not real and offered `spencer@vera.io`
             * while still three characters from finishing.
             *
             * That is a false positive BY CONSTRUCTION, not a tuning problem. A
             * partial TLD is not a typo, it is an incomplete input, and there is no
             * moment during typing at which an unfinished address is evidence of a
             * mistake. A debounce does not fix it either: a pause is still mid-typing,
             * so anyone who hesitates before `.inc` gets the identical warning, just
             * later. The trigger has to be the user's ACTION.
             *
             * So the row is offered as soon as the text is syntactically an address —
             * the user has to be able to REACH it to act on it — and the verdict is
             * withheld until they click it. `questionedInvite` holds the address they
             * clicked; while it equals what is typed, the footer shows the question
             * instead of the row.
             *
             * VRG-471's intent is unchanged: `spencer@usevergence.cin` is still caught
             * before a transfer is written, an owner provisioned and two emails sent
             * nowhere. It is caught at the point of commitment rather than at the point
             * of the fourteenth keystroke.
             */
            listFooter={({ search, close }) => {
              const typed = search.trim()
              const { looksLikeEmail, isDeliverable, reason, suggestion, isQuestionable } =
                inspectRecipient(typed)

              if (!looksLikeEmail) return null

              const alreadyListed = customers.some(
                (customer: Customer) =>
                  customer?.email?.toLowerCase() === typed.toLowerCase(),
              )
              if (alreadyListed) return null

              const pick = (email: string) => {
                setOwner(null)
                setQuestionedInvite(null)
                setInviteEmail(email)
                close()
              }

              // Shared by every row this footer can render, so the invite row and the
              // rows that replace it are the same target at the same height — the
              // second click lands where the first one did.
              const rowSx = {
                borderRadius: 2,
                minHeight: 48,
                '&:hover, &.Mui-focusVisible': {
                  backgroundColor: 'rgba(127, 72, 223, 0.12)',
                },
              }

              // The question is asked about the address the user ACTED on. Comparing
              // against the live text is what makes it self-clearing: edit one
              // character and this is false again, with no reset to remember.
              const isConfirming = questionedInvite === typed && isQuestionable

              if (!isConfirming) {
                return (
                  <ListItem disablePadding>
                    <ListItemButton
                      dense
                      sx={rowSx}
                      onClick={() =>
                        // Clean address: no interstitial. A real ending that resembles
                        // nothing we know is the overwhelmingly common case and must
                        // cost exactly one click (`spencer@vera.inc` is the live
                        // example — `.inc` is real and matches no popular domain).
                        isQuestionable ? setQuestionedInvite(typed) : pick(typed)
                      }
                    >
                      <InvitePreview email={typed} size="medium" />
                    </ListItemButton>
                  </ListItem>
                )
              }

              return (
                <>
                  {/*
                    Why the ending cannot receive mail. Shown only for an ending IANA
                    has never delegated, which is a closed set of facts rather than a
                    guess — a merely SUSPICIOUS address gets the suggestion below and
                    no accusation, because there is nothing here we know to be wrong.
                  */}
                  {/*
                    The shared `Alert`, not bare red text. An undeliverable ending is an
                    ERROR the user has to act on, and the house pattern for that is the
                    design-system banner (§28/§47) — a coloured `Typography` is the one
                    thing in this footer that looked hand-rolled, and it carried none of
                    the Alert's icon, surface or dark-mode handling.
                  */}
                  {!isDeliverable && (
                    <ListItem disablePadding sx={{ px: 2, py: 1.5 }}>
                      <Alert severity="error" description={reason} sx={{ width: '100%' }} />
                    </ListItem>
                  )}

                  {suggestion && suggestion.full !== typed && (
                    <ListItem disablePadding>
                      <ListItemButton dense sx={rowSx} onClick={() => pick(suggestion.full)}>
                        <Typography variant="body3" color="text.secondary">
                          Did you mean{' '}
                          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {suggestion.full}
                          </Box>
                          ?
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  )}

                  {/*
                    SUGGEST, NEVER BLOCK — but only where the product can honour it.
                    A deliverable address that merely resembles a common domain stays
                    one click away: `usevergance.com` may well be somebody's real
                    company, and a guess must not be able to stop a transfer.

                    Deliberately absent for an unreal ending. `@IsDeliverableEmailDomain()`
                    on `TransferRequestDto.toCustomerEmail` rejects it with a 400, so an
                    "anyway" here would not proceed, it would fail one click later with a
                    server error — the late failure working-conventions §46 exists to
                    prevent. The route for a genuinely new TLD is the one the reason line
                    already names: contact support.
                  */}
                  {isDeliverable && (
                    <ListItem disablePadding>
                      <ListItemButton dense sx={rowSx} onClick={() => pick(typed)}>
                        <Typography variant="body3" color="text.secondary">
                          Use{' '}
                          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {typed}
                          </Box>{' '}
                          anyway
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  )}
                </>
              )
            }}
          />
        )}
      </Stack>
    </Modal>
  )
}
